import {
    Additional,
    AdditionalType,
    ApplicationScope, Cart,
    CartItem, MyDbAdditionalDetails, Invoice, InvoiceStatus,
    Order, OrderCartRequest, OrderStatus, PaymentType, PaypalRequest,
    Price,
    Pricing,
    ProductCategory,
    ProductType, Purchase, PurchaseDetail, PurchaseHistoryDetail, PurchaseItem,
    PurchaseItemType, Subscription, SubscriptionChangeRequest, SubscriptionChangeResponse, Voucher
} from "~/api-client";
import {defineStore, storeToRefs} from "pinia";
import {errorMsg} from "~/modules/common/services/util/util";
import {QualifiedPurchasable} from "~/modules/common/stores/products";
/* Aus der QUELLE, nicht aus Nuxts erzeugtem Verzeichnis (30.08.2026):
   `.nuxt/imports` gab es unter Nuxt 3 als Laufzeit-Datei, unter Nuxt 4 nur
   noch als Typdeklaration — der Import liess sich nicht mehr aufloesen.
   Alle uebrigen Dateien holen den Store ohnehin direkt; diese eine war die
   Ausnahme. */
import { useAuthStore } from "~/modules/common/stores/auth";

interface State {
    _orders: Order[],
    _extendedOrders: ExtendedOrder[],
    _orderHistory: Order[],
    _cart: Cart
}

export interface ItemizedOrder extends Order {
    item: CartItem
}

export const useOrderStore = defineStore('order', {
    state: (): State => ({
        _orders: [],
        _extendedOrders: [],
        _orderHistory: [],
        _cart: {},
    }),

    getters: {
        allOrders: (state) => state._orders,
        allExtendedOrders: (state) => state._extendedOrders,
        ownCart: (state) => state._cart,
    },
    actions: {
        async initializeCart(abortInitialization: boolean = false) {
            const {$customerStore, $subscriptionStore} = useNuxtApp();
            const {currentCustomer: customer, orderHistory: orders} = storeToRefs($customerStore);
            await this.getCart();
            // await $customerStore.getCurrentCustomer();
            await $subscriptionStore.getCurrentUserSubscriptions();

            // to avoid free of charge customer to get negative amount
            if (customer.value.freeOfCharge && (!!this.ownCart.vouchers && this.ownCart.vouchers.length > 0)) {
                await this.removeVoucher();
            }

            if ($subscriptionStore.isCurrentUserSubscriptionActive) {
                if (!abortInitialization) {
                    await $customerStore.getOrdersByCustomer(customer.value.id!);
                    // console.log("orders", orders);
                    await this.emptyCart();
                    console.log("this is called empty cart: ", this.$state._cart);
                    if (orders.value?.length > 0) {
                        const carts = orders.value!.find((order: Order) => {
                            let currentSub: Subscription | undefined = $subscriptionStore.currentUserSubscriptionByOrderId(order.id);
                            if (!!currentSub && currentSub.status === "ACTIVE") {
                                return order;
                            }
                        });
                        const currentCart = !!carts ? carts.cart!.items![0] : <CartItem>{};

                        let purchaseItem: PurchaseItem = {
                            productId: currentCart.productId,
                            additionalId: currentCart.productId === currentCart.purchasableId ? undefined : currentCart.purchasableId,
                            type: currentCart.type,
                            quantity: currentCart.quantity
                        }
                        await this.addItemToCart(purchaseItem).then(result => {
                            this.$state._cart = result
                        });
                    }
                }

            }

        },
        getCart() {
            const {$backendService, $logger, $tenant,} = useNuxtApp();
            return $backendService.orderControllerApi.getCart({xApplicationScope: $tenant.getTenant})
                .then((result: Cart) => {
                    this.$state._cart = result;
                    return result;
                }).catch(err => {
                    $logger.error("Error in fetching cart - ", err);
                    throw err;
                });
        },
        addItemToCart(purchaseItem: PurchaseItem): Promise<Cart> {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.orderControllerApi.addItemToCart({
                xApplicationScope: $tenant.getTenant,
                purchaseItem: purchaseItem
            }).then(result => {
                this.$state._cart = result;
                return result;
            }).catch(error => {
                showShoppingCartUpdateError(error);
                throw error;
            })
        },
        deleteItemFromCart(cartItemId: string) {
            const {$backendService, $logger, $tenant} = useNuxtApp();
            return $backendService.orderControllerApi.deleteItemFromCart({
                xApplicationScope: $tenant.getTenant,
                cartItemId: cartItemId
            }).then(result => {
                this.$state._cart = result;
                return result;
            }).catch(error => {
                showShoppingCartUpdateError(error);
                throw error;
            })
        },
        emptyCart() {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.orderControllerApi.emptyCart({xApplicationScope: $tenant.getTenant})
                .then(result => {
                    this.$state._cart = result;
                    return result;
                }).catch(err => {
                    $logger.error("Error in emptying cart - ", err);
                    throw err;
                });
        },
        redeemVoucher(code: string, ignoreError: boolean = false) {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.orderControllerApi.redeemVoucher({
                xApplicationScope: $tenant.getTenant,
                redemption: {
                    voucherCode: code
                }
            }).then(result => {
                this.$state._cart = result;
                return result;
            }).catch(err => {
                if (!ignoreError) {
                    $logger.error("Error in redeeming voucher - ", err);
                    errorMsg(err).then(error_msg => error_msg ?
                        ElMessage.error(error_msg) :
                        ElMessage.error("Es ist ein Fehler beim Aktualisieren des Warenkorbs aufgetreten"));
                    throw err;
                }
            })
        },
        removeVoucher() {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.cartControllerApi.removeVoucher({xApplicationScope: $tenant.getTenant})
                .then(result => {
                    this.$state._cart = result;
                    return result;
                }).catch(error => {
                    errorMsg(error).then(error_msg => error_msg ?
                        ElMessage.error(error_msg) :
                        ElMessage.error("Es ist ein Fehler beim Aktualisieren des Warenkorbs aufgetreten"));
                    throw error;
                })
        },
        async getOrders() {
            const {$backendService, $logger, $tenant, $productStore} = useNuxtApp();
            return $backendService.orderControllerApi.getOrders({
                xApplicationScope: $tenant.getTenant
            }).then(result => {
                let rawOrders = result;
                this.$state._orders = <Order[]>rawOrders;
                rawOrders.forEach(order => buildOrderRowsFromOrder($productStore, <ExtendedOrder>order));
                rawOrders.forEach(order => checkForLatestInvoice(<ExtendedOrder>order))
                this.$state._extendedOrders = <ExtendedOrder[]>rawOrders;
                return <ExtendedOrder[]>rawOrders;
            }).catch(error => {
                $logger.error("error loading orders");// return <Order[]>[];
                ElMessage.error('Die Aufträge konnten nicht geladen werden');
                throw error;
            })
        },
        async getCurrentOrders() {
            const {$backendService, $logger, $tenant, $customerStore, $productStore} = useNuxtApp();

            let rawOrders = await $customerStore.getCurrentOrders();
            const orders: Array<ExtendedCurrentOrder> = [];

            rawOrders.sort((o1, o2) => o1.orderDate.valueOf() - o2.orderDate.valueOf()).forEach(order => orders.push(...buildDetailedOrderFromOrder($productStore, order, $tenant.getTenant)));
            $customerStore._ownExtendedOrders = orders;
            return orders;
        },
        /*
         * `string | undefined` STATT `?` (30.08.2026): Ein Pflicht-Parameter darf
         * nicht hinter einem optionalen stehen — gueltiges TypeScript ist das nie
         * gewesen, der alte Uebersetzer hat es nur geschluckt. Der Uebersetzer von
         * Nuxt 4 weist es zurueck.
         *
         * DIE AUFRUFSTELLE BLEIBT UNVERAENDERT: Der einzige Aufrufer (Basket.vue)
         * uebergibt ohnehin alle drei Werte. `payType` optional zu machen waere
         * der bequemere, aber falsche Weg — er wird gebraucht.
         */
        getOrderCart(referenceSubcriptionId: string | undefined, payType: PaymentType, privateConsent?: { sofortigerBeginn: boolean; widerrufsrecht: boolean }) {
            const {$backendService, $logger, $tenant,} = useNuxtApp();
            let orderCartRequest: OrderCartRequest = {
                paymentType: payType === "INVOICE" || payType === "FREE" ? "INVOICE" : "PAYPAL",
                referenceSubscriptionId: referenceSubcriptionId,
                ...(privateConsent && {
                    privateCustomerConsent: {
                        sofortigerBeginnAccepted: privateConsent.sofortigerBeginn,
                        widerrufsrechtAccepted: privateConsent.widerrufsrecht,
                        acceptedAt: new Date(),
                    }
                }),
            };
            return $backendService.orderControllerApi.orderCart({
                xApplicationScope: $tenant.getTenant,
                orderCartRequest: orderCartRequest
            })
                .then(order => {
                    return order;
                }).catch(error => {
                    console.log('ORDER_CART_ERROR', error);
                    ElMessage.error('Es ist ein Fehler bei der Kaufabwicklung aufgetreten');
                    throw error;
                });
        },
        cancelOrder(orderId: string) {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.orderControllerApi.cancelOrder({
                xApplicationScope: $tenant.getTenant,
                id: orderId
            }).catch(error => {
                ElMessage.error('Der Auftrag konnte nicht storniert werden');
                throw error;
            })
        },
        getSubscriptionsForOrder(orderId: string) {
            const {$backendService, $logger, $tenant, $subscriptionStore} = useNuxtApp();

            return $backendService.orderControllerApi.getSubscriptionsForOrder({
                xApplicationScope: $tenant.getTenant,
                id: orderId
            }).then(result => {
                $subscriptionStore._currentUserSubscriptions = result;
            }).catch(error => {
                ElMessage.error("Bei der Aktualisierung der Tabelle ist ein Fehler aufgetreten");
                throw error;
            });
        },
        startPaypalPaymentProcess(paypalRequest: PaypalRequest) {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.orderControllerApi.startPaypalPaymentProcess({
                xApplicationScope: $tenant.getTenant,
                paypalRequest: paypalRequest
            }).then(result => {
                return result;
            }).catch(error => {
                ElMessage.error("Der Bezahlvorgang konnte nicht gestartet werden, bitte versuchen Sie es später erneut.");
                throw error;
            })
        },
        updatePaypalPaymentProcess(subscription_id: string, token: string, waitForRole?: string) {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.orderControllerApi.updatePaypalPaymentProcess({
                xApplicationScope: $tenant.getTenant,
                paypalUpdateRequest: {
                    token: <string>subscription_id ?? token
                }
            }).then((order) => {
                if (waitForRole && subscription_id) {
                    const authStore = useAuthStore();
                    let counter = 0;

                    function userInfoRefresh(resolve: (val: Order) => void) {
                        console.log('trigger user info refresh');
                        authStore.updateUser().then(() => {
                            if (authStore.currentUser?.roles?.includes(waitForRole.toUpperCase())) {
                                // role present => resolve promise
                                resolve(order);
                                return;
                            }

                            // if we reach this point, the token was not present
                            counter++;

                            if (counter > 10) {
                                ElMessage.error('Bei der Bereitstellung Ihres gewünschten Produktes kommt es momentan zu einer Verzögerung. Wir arbeiten so schnell wie möglich daran und bitten um Ihre Geduld.');
                                resolve(order);
                                return;
                            }

                            console.log('waiting for 5 seconds for next check');
                            setTimeout(() => userInfoRefresh(resolve), 5000);
                        })
                    }

                    return new Promise(userInfoRefresh);
                } else {
                    return order;
                }
            }).catch(error => {
                console.log('error using payment provider', error);
                ElMessage.error('Informationen über Ihren Kauf konnten nicht mit dem Zahlungsdienstleister abgeglichen werden, bitte wenden Sie sich an den Support.');
            });
        },
        updatePaymentProcessForInvoicePayment(waitForRole?: string){
            if (waitForRole) {
                const authStore = useAuthStore();
                let counter = 0;

                function userInfoRefresh(resolve: (val: any) => void) {
                    console.log('trigger user info refresh');
                    authStore.updateUser().then(() => {
                        if (authStore.currentUser?.roles?.includes(waitForRole.toUpperCase())) {
                            // role present => resolve promise
                            resolve(null);
                            return;
                        }

                        // if we reach this point, the token was not present
                        counter++;

                        if (counter > 10) {
                            ElMessage.error('Bei der Bereitstellung Ihres gewünschten Produktes kommt es momentan zu einer Verzögerung. Wir arbeiten so schnell wie möglich daran und bitten um Ihre Geduld.');
                            resolve(null);
                            return;
                        }

                        console.log('waiting for 5 seconds for next check');
                        setTimeout(() => userInfoRefresh(resolve), 5000);
                    })
                }

                return new Promise(userInfoRefresh);
            }
        },
        async getProrationForSubscription(qualifiedPurchasable: QualifiedPurchasable, runningSubscriptionId: string) {
            const {$backendService, $logger, $tenant} = useNuxtApp();
            return $backendService.orderControllerApi.getProrationForSubscription({
                xApplicationScope: $tenant.getTenant,
                subscriptionChangeRequest: {
                    quantity: qualifiedPurchasable.quantity,
                    productId: qualifiedPurchasable.purchasable.id,
                    referenceSubscriptionId: runningSubscriptionId
                }
            })
                .then((response: SubscriptionChangeResponse) => {
                    return response;
                }).catch(error => {
                    console.log('error fetching subscription change response: ', error);
                    ElMessage.error('Beim Abruf der Proration-Information ist zu einem Fehler aufgetreten.');
                });
        },
        updateOrderStatus(order: Order | ExtendedOrder | ExtendedCurrentOrder, orderStatus: OrderStatus) {
            order.status = orderStatus;
        },
        updateInvoiceStatus(order: Order | ExtendedOrder | ExtendedCurrentOrder, invoiceStatus: InvoiceStatus) {
            if (!!order.invoice) {
                order.invoice.status = invoiceStatus;
            }
        },
        updateInvoiceStatusOnAllOrdersState(orderId: string, invoiceStatus: InvoiceStatus) {
            this.$state._extendedOrders = this.$state._extendedOrders.map(order => {
                if (order.id === orderId) {
                    this.updateInvoiceStatus(order, invoiceStatus);
                }
                return order;
            });
        },
        cancelOrderStatusAndInvoiceStatusInAllOrdersState(orderId: string) {
            this.$state._extendedOrders = this.$state._extendedOrders.map(order => {
                if (order.id === orderId) {
                    this.updateOrderStatus(order, OrderStatus.Cancelled);
                    this.updateInvoiceStatus(order, InvoiceStatus.Cancelled)
                }
                return order;
            });

            this.$state._orders = this.$state._orders.map(order => {
                if (order.id === orderId) {
                    this.updateOrderStatus(order, OrderStatus.Cancelled);
                    this.updateInvoiceStatus(order, InvoiceStatus.Cancelled)
                }
                return order;
            })
        }
    }
})


//CURRENT ORDERS FOR SUPERADMINS
export interface ExtendedCartItem extends CartItem {
    purchasedAdditionals?: ExtendedCartItem[],
    orderDate?: string,
    description?: string,
    name?: string,
    productCategory?: ProductCategory,
    pricing?: Pricing,
    productType?: ProductType,
    totalPrice?: Price
}

export interface ExtendedOrder extends Order {
    extendedInformation: {
        products: ExtendedCartItem[],
        orderDate: Date,
        invoiceDate: Date,
        organisationName: string,
        requestedStatusChange?: boolean,
    }
}

export function checkForLatestInvoice(order: ExtendedOrder){
    if (!!order.latestRecurringInvoice && order.subscription && order.latestRecurringInvoice.id != order.invoice?.id){
        return order.invoice = order.latestRecurringInvoice;
    }
    return;
}

export function buildOrderRowsFromOrder(products: any, order: ExtendedOrder) {
    const purchasedProducts: { [id: string]: ExtendedCartItem } = {};
    const extendedInformation = {
        refunded: !!order.refundId,
        status: order.status,
        products: <ExtendedCartItem[]>[],
        orderDate: new Date(order.orderDate),
        invoiceDate: !!order.invoice?.invoiceDate ? new Date(order.invoice.invoiceDate) : new Date(order.orderDate),
        organisationName: !!order.customer?.organisation ? order.customer!.organisation.name : '-',
    };

    (<ExtendedCartItem[]>order.cart!.items!).forEach(item => {
        if (item.type === PurchaseItemType.Product) {
            const purchasable = products.purchasableById(item.purchasableId);
            purchasedProducts[item.purchasableId] = <ExtendedCartItem>item;
            item.description = purchasable.description;
            item.name = purchasable.name;
            item.productCategory = purchasable.productCategory;
            item.pricing = Pricing.Linear;
            item.price = purchasable.price!;
            item.productType = purchasable.productType;
            extendedInformation.products.push(item);
        }
    });

    return order.extendedInformation = extendedInformation;
}


//CURRENT ORDERS FOR CUSTOMERS
interface ExtendedPurchaseDetail extends PurchaseDetail {
    details?: Additional
}

interface ProductDetails {
    id?: string,
    description?: string,
    name?: string,
    pricing?: Pricing,
    price?: Price,
    productType?: ProductType,
    printChecks?: ExtendedPurchaseDetail[],
    prints?: ExtendedPurchaseDetail[],
    shipping?: ExtendedPurchaseDetail[]
}

export interface ExtendedCurrentOrder {
    id?: string,
    orderDate?: Date,
    invoice?: Invoice,
    invoiceDate?: Date,
    allPurchases?: PurchaseDetail[],
    product?: ProductDetails,
    checkPurchased?: boolean,
    printsPurchased?: number,
    shippingPurchased?: number,
    status?: OrderStatus,
}

function buildDetailedOrderFromOrder(productStore: any, order: Order, tenant: ApplicationScope) {
    const purchasesByProduct: { [id: string]: Purchase[] } = {};

    if (!!(<MyDbAdditionalDetails>order.tenantDetails).purchases) {
        (<MyDbAdditionalDetails>order.tenantDetails).purchases?.forEach(purchase => {
            const mainProductPurchase = purchase.purchaseDetails?.find(p => p.purchaseItemType === PurchaseItemType.Product);

            if (!!mainProductPurchase) {
                if (!purchasesByProduct[mainProductPurchase.productId]) {
                    purchasesByProduct[mainProductPurchase.productId] = [];
                }

                purchasesByProduct[mainProductPurchase.productId].push(purchase);
            }
        });
    }

    const orderSubItems: ExtendedCurrentOrder[] = [];

    Object.keys(purchasesByProduct).forEach(purchaseGroup => {
        const detailedOrderRow: ExtendedCurrentOrder = {};
        detailedOrderRow.id = order.id;
        detailedOrderRow.orderDate = new Date(order.orderDate);
        detailedOrderRow.invoice = order.invoice;
        detailedOrderRow.invoiceDate = !!order.invoice?.invoiceDate ? new Date(order.invoice.invoiceDate) : new Date(order.orderDate);

        const productDetails: ProductDetails = {};

        const data = purchasesByProduct[purchaseGroup];
        detailedOrderRow.allPurchases = data[0].purchaseDetails;

        const mainProductPurchase = data[0].purchaseDetails?.find(p => p.purchaseItemType === PurchaseItemType.Product);
        const mainProductDetails = productStore.productById(mainProductPurchase!.productId);

        productDetails.id = mainProductDetails.id;
        productDetails.description = mainProductDetails.description;
        productDetails.name = mainProductDetails.name;
        productDetails.pricing = Pricing.Linear;
        productDetails.price = mainProductDetails.price;
        productDetails.productType = mainProductDetails.productType;

        detailedOrderRow.product = productDetails;

        orderSubItems.push(detailedOrderRow);
    });

    return orderSubItems;
}

export const showShoppingCartUpdateError = (error: any) => {
    const {$logger} = useNuxtApp();
    $logger.info('UPDATE_SHOPPING_CART_ERROR', error);
    ElMessage.error('Es ist ein Fehler beim Aktualisieren des Warenkorbs aufgetreten');
}

export const checkRoute = async () => {
    const route = useRoute();
    const token = route.query.token;
    const mode = route.query.mode;
    const status = route.query.status;
    const subscription_id = route.query.subscription_id;

    if (!!token && mode === 'paypal') {
        try {
            // TODO adjust this for all portals!!!
            await useOrderStore().updatePaypalPaymentProcess(subscription_id!.toString(), token.toString(), "DB_OWNER");
            if (status === 'success') {
                ElMessage.success('Der Bezahlvorgang war erfolgreich.');
            } else if (status === 'cancel') {
                ElMessage.error('Der Bezahlvorgang wurde abgebrochen.');
            }
        } catch (error) {
            console.log('error', error);
            ElMessage.error('Informationen über Ihren Kauf konnten nicht mit dem Zahlungsdienstleister abgeglichen werden, bitte wenden Sie sich an den Support.');
        }
    }
}

export const mapQualifiablePurchasableToPurchaseItem = (purchasableToBeMapped: QualifiedPurchasable) => {
    return <PurchaseItem>{
        productId: !!purchasableToBeMapped.purchasable.parentProductId ? purchasableToBeMapped.purchasable.parentProductId : purchasableToBeMapped.purchasable.id,
        additionalId: !!purchasableToBeMapped.purchasable.parentProductId ? purchasableToBeMapped.purchasable.id : undefined,
        type: purchasableToBeMapped.purchasable.type,
        quantity: !purchasableToBeMapped.quantity ? 1 : purchasableToBeMapped.quantity
    }
}
